import { Injectable, inject } from '@angular/core';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { Observable } from 'rxjs';
import { BlogPostSelectorComponent } from '../components/blog-post-selector/blog-post-selector.component';
import {
  BlogPostSelectorConfig,
  BlogPostSelectorResult,
  BlogPostSelectorItem,
  SelectedBlogPost
} from '../../models/blog-post-selector.model';
import { BlogPost } from '../../models/blog-post.model';

/**
 * Service for opening the BlogPostSelectorComponent as a dialog
 * 
 * Provides a convenient API for programmatically opening the blog post selector
 * and handling the results.
 * 
 * @example
 * Basic usage:
 * ```typescript
 * constructor(private blogPostSelector: BlogPostSelectorService) {}
 * 
 * openSelector() {
 *   const dialogRef = this.blogPostSelector.open({
 *     blogPosts: this.posts,
 *     title: 'Select Related Posts'
 *   });
 * 
 *   dialogRef.closed.subscribe(result => {
 *     if (result && !result.cancelled) {
 *       console.log('Selected posts:', result.selectedPosts);
 *     }
 *   });
 * }
 * ```
 * 
 * @example
 * With pre-selection:
 * ```typescript
 * openSelectorWithPreselection() {
 *   const dialogRef = this.blogPostSelector.open({
 *     blogPosts: this.posts,
 *     preSelectedIds: ['post-id-1', 'post-id-2'],
 *     maxSelection: 5
 *   });
 * 
 *   dialogRef.closed.subscribe(result => {
 *     if (result && !result.cancelled) {
 *       this.updateSelectedPosts(result.selectedPosts);
 *     }
 *   });
 * }
 * ```
 * 
 * @example
 * Using the convenience method with full BlogPost objects:
 * ```typescript
 * selectFromBlogPosts() {
 *   this.blogPostSelector.openWithBlogPosts(this.fullPosts, {
 *     title: 'Link Blog Posts',
 *     preSelectedIds: this.currentLinkedPostIds,
 *     maxSelection: 10
 *   }).subscribe(selectedPosts => {
 *     if (selectedPosts) {
 *       this.linkedPosts = selectedPosts;
 *     }
 *   });
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class BlogPostSelectorService {
  private dialog = inject(Dialog);

  /**
   * Open the blog post selector dialog
   * 
   * @param config - Configuration for the selector dialog
   * @returns DialogRef that can be used to track the dialog state and get results
   */
  open(config: BlogPostSelectorConfig): DialogRef<BlogPostSelectorResult, BlogPostSelectorComponent> {
    return this.dialog.open<BlogPostSelectorResult, BlogPostSelectorConfig, BlogPostSelectorComponent>(
      BlogPostSelectorComponent,
      {
        data: config,
        disableClose: false,
        hasBackdrop: true,
        backdropClass: 'blog-post-selector-backdrop',
        panelClass: 'blog-post-selector-panel',
        width: '100%',
        maxWidth: '48rem', // 768px - matches max-w-3xl
        maxHeight: '90vh'
      }
    );
  }

  /**
   * Convenience method to open selector with a search function
   * 
   * @param searchFunction - Function that searches and returns blog posts
   * @param organizationId - Organization ID for scoped search
   * @param options - Additional configuration options (optional)
   * @returns Observable that emits selected posts or null if cancelled
   */
  openWithSearch(
    searchFunction: (criteria: any) => Observable<BlogPostSelectorItem[]>,
    organizationId: string,
    options?: Partial<Omit<BlogPostSelectorConfig, 'searchFunction' | 'organizationId'>>
  ): Observable<SelectedBlogPost[] | null> {
    const config: BlogPostSelectorConfig = {
      searchFunction,
      organizationId,
      ...options
    };

    const dialogRef = this.open(config);

    return new Observable<SelectedBlogPost[] | null>(observer => {
      const subscription = dialogRef.closed.subscribe(result => {
        if (result && !result.cancelled) {
          observer.next(result.selectedPosts);
        } else {
          observer.next(null);
        }
        observer.complete();
      });

      return () => subscription.unsubscribe();
    });
  }

  /**
   * Convenience method that returns a Promise instead of Observable
   * 
   * @param config - Configuration for the selector dialog
   * @returns Promise that resolves to selected posts or null if cancelled
   */
  async openAsync(config: BlogPostSelectorConfig): Promise<SelectedBlogPost[] | null> {
    const dialogRef = this.open(config);
    const result = await dialogRef.closed.toPromise();
    
    if (result && !result.cancelled) {
      return result.selectedPosts;
    }
    
    return null;
  }

  /**
   * Convenience method with search function that returns a Promise
   * 
   * @param searchFunction - Function that searches and returns blog posts
   * @param organizationId - Organization ID for scoped search
   * @param options - Additional configuration options (optional)
   * @returns Promise that resolves to selected posts or null if cancelled
   */
  async openWithSearchAsync(
    searchFunction: (criteria: any) => Observable<BlogPostSelectorItem[]>,
    organizationId: string,
    options?: Partial<Omit<BlogPostSelectorConfig, 'searchFunction' | 'organizationId'>>
  ): Promise<SelectedBlogPost[] | null> {
    const config: BlogPostSelectorConfig = {
      searchFunction,
      organizationId,
      ...options
    };

    return this.openAsync(config);
  }
}
